import { useSelector } from 'react-redux'

import Button from '@/components/common/Button'
import Loading from '@/components/common/Loading'
import useAuth from '@/hooks/useAuth'
import { selectSectionSettingData } from '@/redux/customisation/selector'
import { selectDetailMenuSite } from '@/redux/meta/selector'
import { useTranslate } from '@tolgee/react'
import {
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  isLight,
} from 'oddle-shop-monorepo.design-system'
import { usePreviewNavigationContext } from '@/contexts/PreviewNavigationProvider'

export const pagesArray = [
  {
    id: 'addressBook',
    name: 'Address Book',
    as: '/user/addresses',
  },
  {
    id: 'accountDetails',
    name: 'Account Details',
    as: '/user/details',
  },
  {
    id: 'paymentMethods',
    name: 'Payment Methods',
    as: '/user/payment-methods',
  },
  {
    id: 'orderHistory',
    name: 'Order History',
    as: '/user/orders',
  },
]

const UserButton = ({
  truncate,
  onSignInClick,
}: {
  truncate?: boolean
  onSignInClick?: () => void
}) => {
  const { t } = useTranslate()
  const { data, isLoading, isFetching } = useAuth()
  const { menu, features } = useSelector(selectDetailMenuSite) || {}
  const sectionSettingData = useSelector(selectSectionSettingData)
  const { pushWithPreview } = usePreviewNavigationContext()

  const { settings: customisationSettings = {} } =
    sectionSettingData?.sections?.['header'] || {}

  const { navigationLinkFontColor } = customisationSettings

  const handleClickLogin = async () => {
    if (menu?.id) {
      // Call the callback to close drawer if provided
      if (onSignInClick) {
        onSignInClick()
        // Wait a bit to ensure drawer is fully closed before opening iframe
        setTimeout(async () => {
          await window.opw.signIn(menu.id)
        }, 300)
      } else {
        await window.opw.signIn(menu.id)
      }
    }
  }

  if (isLoading || isFetching) {
    return (
      <Button className="text-base font-bold" disabled>
        <Loading className="h-5 w-5" />
      </Button>
    )
  }

  if (!data?.data?.payload?.firstName) {
    return (
      <Button
        variant={truncate ? 'link' : 'default'}
        className={
          truncate
            ? 'text-header-link-color w-fit px-2 text-lg'
            : cn(
                'bg-header-link-color text-header-background-color border-none !shadow-none',
                isLight(navigationLinkFontColor)
                  ? 'hover:bg-header-link-color-darker'
                  : 'hover:bg-header-link-color-lighter'
              )
        }
        onClick={handleClickLogin}
      >
        {t('Sign In')}
      </Button>
    )
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className="hover:text-primary flex h-full items-center gap-2 px-4 text-sm font-bold outline-none"
        asChild
      >
        <Button
          className={cn(
            'bg-header-link-color text-header-background-color border-none !shadow-none',
            isLight(navigationLinkFontColor)
              ? 'hover:bg-header-link-color-darker'
              : 'hover:bg-header-link-color-lighter'
          )}
        >
          {t('Hi, {name}', { name: data.data.payload.firstName })}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="bg-header-background-color">
        {pagesArray
          .filter((item) => {
            if (features?.enableCustomerSaveCreditCardOnStripe) return true
            return item.id !== 'paymentMethods'
          })
          .map((item) => (
            <DropdownMenuItem
              key={item.id}
              onClick={(e) => {
                e.preventDefault()
                pushWithPreview(item.as)
              }}
              className="hover:text-header-link-color cursor-pointer text-base font-bold outline-none"
            >
              {t(item.name)}
            </DropdownMenuItem>
          ))}
        <DropdownMenuItem
          onClick={async (e) => {
            e.preventDefault()
            await window.opw.signOut(data.data.payload.ocaId)
            window.location.reload()
          }}
          className="hover:text-header-link-color cursor-pointer text-base font-bold outline-none"
        >
          {t('Log out')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export default UserButton
